import React, { type ButtonHTMLAttributes } from 'react'
import styled, { type CSSObject } from '@emotion/styled'
import { colors, radius, shadow, spacing, font } from '@mobi/component-library/Theme/Common'
import { gapX } from '@mobi/component-library/Utils/gap'
import { LoadingSpinner } from '@mobi/component-library/Feedback/LoadingSpinner'

type Props = Partial<ButtonStyledProps> & ButtonHTMLAttributes<HTMLButtonElement>

export const Button = React.forwardRef<HTMLButtonElement, Props>(
  (
    {
      children,
      color = 'primary',
      isFullWidth = false,
      size = 'md',
      disabled = false,
      isLoading = false,
      ...props
    },
    ref
  ) => (
    <ButtonStyled
      ref={ref}
      color={color}
      isFullWidth={isFullWidth}
      size={size}
      disabled={isLoading || disabled}
      isLoading={isLoading}
      {...props}
    >
      {isLoading ? (
        <LoadingSpinner size='2rem' color={color === 'positive' ? 'white' : 'purple'} />
      ) : (
        children
      )}
    </ButtonStyled>
  )
)

Button.displayName = 'Button'

// #region Styles

export const ButtonStyled = styled.button<ButtonStyledProps>(
  {
    boxSizing: 'border-box',
    justifyContent: 'center',
    alignItems: 'center',
    fontFamily: font.family.primary,
    cursor: 'pointer',
    touchAction: 'manipulation',
    overflow: 'hidden',
    transition: 'all 0.25s ease',
    userSelect: 'none',
    WebkitTapHighlightColor: colors.transparent,

    '&:disabled': {
      opacity: 1,
      pointerEvents: 'none',
    },

    '&:active': {
      transform: 'scale(0.95)',
    },
  },
  ({ color = 'primary', size = 'md', isFullWidth = false, isIconOnlyButton = false }) => ({
    ...(isFullWidth ? { display: 'flex', width: '100%' } : { display: 'inline-flex' }),

    ...buttonSizing({ isIconOnlyButton })[size],
    ...buttonColors[color],
  }),
  gapX('0.8rem')
)

export const buttonSizing = ({
  isIconOnlyButton = false /* For single-icon only buttons, horizontal and vertical padding should match. */,
  isPillButton = false,
}): Readonly<Record<ButtonStyledProps['size'], CSSObject>> => ({
  xxs: {
    height: '2.8rem',
    minWidth: '2.8rem',
    padding: `0 ${isIconOnlyButton ? 0 : spacing.smx1}`,
    fontWeight: font.weight.medium,
    fontSize: font.size.md.fontSize,
    letterSpacing: font.size.md.letterSpacing,
    lineHeight: font.size.md.lineHeight,
    borderRadius: isPillButton ? radius.pill : radius.sm,
  },
  xs: {
    height: '3.2rem',
    minWidth: '3.2rem',
    padding: `0 ${isIconOnlyButton ? 0 : spacing.sm}`,
    fontWeight: font.weight.medium,
    fontSize: font.size.lg.fontSize,
    letterSpacing: font.size.lg.letterSpacing,
    lineHeight: font.size.lg.lineHeight,
    borderRadius: isPillButton ? radius.pill : radius.sm,
  },
  sm: {
    height: '3.6rem',
    minWidth: '3.6rem',
    padding: `0 ${isIconOnlyButton ? 0 : spacing.sm}`,
    fontWeight: font.weight.medium,
    fontSize: font.size.lg.fontSize,
    letterSpacing: font.size.lg.letterSpacing,
    lineHeight: font.size.lg.lineHeight,
    borderRadius: isPillButton ? radius.pill : radius.lg,
  },
  md: {
    height: '4.4rem',
    minWidth: '4.4rem',
    padding: `0 ${isIconOnlyButton ? 0 : spacing.md}`,
    fontWeight: font.weight.medium,
    fontSize: font.size.lg.fontSize,
    letterSpacing: font.size.lg.letterSpacing,
    lineHeight: font.size.lg.lineHeight,
    borderRadius: isPillButton ? radius.pill : radius.lgx1,
  },
  lg: {
    height: '5.6rem',
    minWidth: '5.6rem',
    padding: `0 ${isIconOnlyButton ? 0 : spacing.bigx1}`,
    fontWeight: font.weight.semibold,
    fontSize: font.size.xl.fontSize,
    letterSpacing: font.size.xl.letterSpacing,
    lineHeight: font.size.xl.lineHeight,
    borderRadius: isPillButton ? radius.pill : radius.lgx2,
  },
})

export const buttonColors: Readonly<Record<ButtonStyledProps['color'], CSSObject>> = {
  primary: {
    color: colors.white,
    backgroundColor: colors.studio[500],
    border: `0.1rem solid ${colors.studio[500]}`,
    boxShadow: shadow.xs,

    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        background: colors.studio[600],
        border: `0.1rem solid ${colors.studio[600]}`,
      },
    },

    '&:active': {
      color: `color(from ${colors.white} srgb r g b / 0.75)`,
      background: colors.studio[700],
      border: `0.1rem solid ${colors.studio[700]}`,
    },

    '&:focus-visible': {
      border: `0.2rem solid ${colors.studio[200]}`,
    },

    '&:disabled': {
      background: colors.lavender[200],
      border: `0.1rem solid ${colors.lavender[200]}`,
    },
  },

  primary_destructive: {
    color: colors.white,
    backgroundColor: colors.error[600],
    border: `0.1rem solid ${colors.error[600]}`,
    boxShadow: shadow.xs,

    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        backgroundColor: colors.error[700],
        border: `0.1rem solid ${colors.error[700]}`,
      },
    },

    '&:active': {
      color: `color(from ${colors.white} srgb r g b / 0.75)`,
      backgroundColor: colors.error[800],
      border: `0.1rem solid ${colors.error[800]}`,
    },

    '&:focus-visible': {
      backgroundColor: colors.error[600],
      border: `0.2rem solid ${colors.error[300]}`,
    },

    '&:disabled': {
      backgroundColor: colors.crimson[100],
      border: `0.1rem solid ${colors.crimson[100]}`,
    },
  },

  secondary_color: {
    color: colors.lavender[700],
    backgroundColor: colors.lavender[50],
    border: `0.1rem solid ${colors.lavender[50]}`,

    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        backgroundColor: colors.lavender[100],
        border: `0.1rem solid ${colors.lavender[100]}`,
      },
    },

    '&:active': {
      color: `color(from ${colors.lavender[700]} srgb r g b / 0.75)`,
      backgroundColor: colors.lavender[200],
      border: `0.1rem solid ${colors.lavender[200]}`,
    },

    '&:focus-visible': {
      backgroundColor: colors.lavender[50],
      border: `0.2rem solid ${colors.lavender[200]}`,
    },

    '&:disabled': {
      color: colors.studio[300],
      backgroundColor: colors.lavender[50],
      border: `0.1rem solid ${colors.lavender[50]}`,
    },
  },

  secondary_grey: {
    color: colors.neutral[900],
    backgroundColor: colors.surface[200],
    border: `0.1rem solid ${colors.surface[200]}`,

    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        backgroundColor: colors.neutral[300],
        border: `0.1rem solid ${colors.neutral[300]}`,
      },
    },

    '&:active': {
      color: `color(from ${colors.neutral[900]} srgb r g b / 0.75)`,
      backgroundColor: colors.neutral[400],
      border: `0.1rem solid ${colors.neutral[400]}`,
    },

    '&:focus-visible': {
      border: `0.2rem solid ${colors.neutral[400]}`,
    },

    '&:disabled': {
      color: colors.neutral[500],
      backgroundColor: colors.surface[50],
      border: `0.1rem solid ${colors.surface[50]}`,
    },
  },

  secondary_outline: {
    color: colors.neutral[900],
    backgroundColor: colors.white,
    border: `0.1rem solid ${colors.neutral[200]}`,
    boxShadow: shadow.xs,

    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        backgroundColor: colors.neutral[100],
        border: `0.1rem solid ${colors.neutral[100]}`,
      },
    },

    '&:active': {
      color: `color(from ${colors.neutral[900]} srgb r g b / 0.75)`,
      backgroundColor: colors.neutral[200],
      border: `0.1rem solid ${colors.neutral[200]}`,
    },

    '&:focus-visible': {
      border: `0.2rem solid ${colors.neutral[500]}`,
    },

    '&:disabled': {
      color: colors.neutral[500],
      border: `0.1rem solid ${colors.neutral[200]}`,
    },
  },

  secondary_destructive: {
    backgroundColor: colors.error[50],
    border: `0.1rem solid ${colors.error[50]}`,
    boxShadow: shadow.xs,
    color: colors.error[700],

    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        backgroundColor: colors.error[200],
        border: `0.1rem solid ${colors.error[200]}`,
      },
    },

    '&:active': {
      color: `color(from ${colors.error[50]} srgb r g b / 0.75)`,
      backgroundColor: colors.error[300],
      border: `0.1rem solid ${colors.error[300]}`,
    },

    '&:focus-visible': {
      backgroundColor: colors.error[50],
      border: `0.2rem solid ${colors.error[300]}`,
    },

    '&:disabled': {
      backgroundColor: colors.crimson[50],
      border: `0.1rem solid ${colors.crimson[50]}`,
      color: colors.crimson[200],
    },
  },

  secondary_dark: {
    color: colors.lavender[50],
    backgroundColor: colors.lavender[800],
    border: `0.1rem solid ${colors.lavender[800]}`,
    boxShadow: shadow.xs,

    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        background: colors.lavender[600],
        border: `0.1rem solid ${colors.lavender[600]}`,
      },
    },

    '&:active': {
      color: `color(from ${colors.white} srgb r g b / 0.75)`,
      background: colors.lavender[700],
      border: `0.1rem solid ${colors.lavender[700]}`,
    },

    '&:focus-visible': {
      border: `0.2rem solid ${colors.lavender[200]}`,
    },

    '&:disabled': {
      background: colors.lavender[200],
      border: `0.1rem solid ${colors.lavender[200]}`,
    },
  },

  tertiary_color: {
    color: colors.lavender[700],
    backgroundColor: colors.transparent,
    border: 0,

    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        backgroundColor: colors.lavender[50],
      },
    },

    '&:active': {
      color: `color(from ${colors.lavender[700]} srgb r g b / 0.75)`,
      backgroundColor: colors.lavender[100],
    },

    '&:focus-visible': {
      border: `0.2rem solid ${colors.lavender[50]}`,
    },

    '&:disabled': {
      color: colors.studio[200],
    },
  },

  tertiary_grey: {
    color: colors.neutral[900],
    backgroundColor: colors.transparent,
    border: 0,

    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        backgroundColor: colors.surface[100],
      },
    },

    '&:active': {
      color: `color(from ${colors.neutral[900]} srgb r g b / 0.75)`,
      backgroundColor: colors.surface[400],
    },

    '&:focus-visible': {
      border: `0.2rem solid ${colors.neutral[400]}`,
    },

    '&:disabled': {
      color: colors.neutral[500],
    },
  },

  tertiary_dark: {
    color: colors.white,
    backgroundColor: colors.transparent,
    border: 0,

    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        color: colors.lavender[50],
        backgroundColor: colors.studio[800],
      },
    },

    '&:active': {
      color: colors.studio[200],
      backgroundColor: colors.studio[800],
    },

    '&:focus-visible': {
      border: `0.2rem solid ${colors.studio[200]}`,
      backgroundColor: colors.studio[800],
    },

    '&:disabled': {
      color: colors.neutral[800],
    },
  },

  positive: {
    color: colors.white,
    backgroundColor: colors.success[500],
    border: `0.1rem solid ${colors.success[500]}`,
    boxShadow: shadow.xs,

    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        backgroundColor: colors.success[600],
        border: `0.1rem solid ${colors.success[600]}`,
      },
    },

    '&:active': {
      color: `color(from ${colors.white} srgb r g b / 0.75)`,
      backgroundColor: colors.success[700],
      border: `0.1rem solid ${colors.success[700]}`,
    },

    '&:focus-visible': {
      backgroundColor: colors.success[500],
      border: `0.2rem solid ${colors.success[200]}`,
    },

    '&:disabled': {
      backgroundColor: colors.success[200],
      border: `0.1rem solid ${colors.success[200]}`,
    },
  },
}
// #endregion

// #region Types
export type ButtonColor =
  | 'primary'
  | 'primary_destructive'
  | 'secondary_color'
  | 'secondary_grey'
  | 'secondary_outline'
  | 'secondary_destructive'
  | 'secondary_dark'
  | 'tertiary_color'
  | 'tertiary_grey'
  | 'tertiary_dark'
  | 'positive'

type ButtonStyledProps = {
  /**
   * The color variant of the button
   */
  color: ButtonColor
  /**
   * The size of the button
   */
  size: 'xxs' | 'xs' | 'sm' | 'md' | 'lg'
  /**
   * Whether the button should be inline or block-level
   *
   * Setting this to true will make the button block-level and set its width to 100%
   */
  isFullWidth?: boolean
  /**
   *  Whether the button's content consists of a single icon without any text
   */
  isIconOnlyButton?: boolean
  /**
   * Whether the button is in a loading state. Buttons in a loading state show the loading indicator and are disabled.
   */
  isLoading?: boolean
}
// #endregion
